import {
  CheckCircle,
  CreditCard,
  Image as ImageIcon,
  ShoppingBag,
  Warning,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createOrderPayment } from "../services/orderService";
import type { Order, Package, Photo } from "../types/api";

import { usePackageImages } from "../hooks/usePackageImages";

const Checkout: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  // State management
  const [order, setOrder] = useState<Order | null>(null);
  const [pkg, setPackage] = useState<Package | null>(null);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [packages, setPackages] = useState<Package[]>([]);
  const [selectedPackageId, setSelectedPackageId] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [redirecting, setRedirecting] = useState(false);
  const [creatingPayment, setCreatingPayment] = useState(false);
  const [orderNote, setOrderNote] = useState<string>("");

  // Preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState<string>("");
  const [previewAlt, setPreviewAlt] = useState<string>("");

  // Load package images (with fallback to static imports)
  const { previewImage: pkgPreviewImage, packageImage: pkgImage } =
    usePackageImages(pkg);

  // Fetch order, package, and photos on mount
  useEffect(() => {
    const fetchCheckoutData = async () => {
      if (!token) {
        setError("Invalid checkout link - missing token");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        // Fetch available packages
        const packagesResponse = await fetch(`/api/packages?active=true`);
        if (packagesResponse.ok) {
          const packagesData = await packagesResponse.json();
          setPackages(packagesData);
        }

        // Fetch order and package
        const orderResponse = await fetch(`/api/orders/public/${token}`);

        if (!orderResponse.ok) {
          const errorData = await orderResponse.json();
          if (orderResponse.status === 410) {
            // Already paid
            setError("This order has already been paid. Redirecting...");
            setTimeout(() => {
              navigate(`/payment/success/${token}`);
            }, 2000);
            return;
          }
          throw new Error(errorData.error || "Failed to load order");
        }

        const checkoutData = await orderResponse.json();
        setOrder(checkoutData.order);
        if (checkoutData.package) {
          setPackage(checkoutData.package);
          setSelectedPackageId(checkoutData.package.packageId);
        } else if (packages.length > 0) {
          // Pre-select first package if none is assigned yet
          setSelectedPackageId(packages[0].packageId);
        }

        // Fetch photos
        const photosResponse = await fetch(
          `/api/orders/public/${token}/photos`,
        );

        if (photosResponse.ok) {
          const photosData = await photosResponse.json();
          setPhotos(photosData);
        }
      } catch (err) {
        console.error("Error fetching checkout data:", err);
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load checkout information",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCheckoutData();
  }, [token, navigate]);

  // Fetch selected package details when selectedPackageId changes
  useEffect(() => {
    if (selectedPackageId && packages.length > 0) {
      const selectedPkg = packages.find(
        (p) => p.packageId === selectedPackageId,
      );
      if (selectedPkg) {
        setPackage(selectedPkg);
      }
    }
  }, [selectedPackageId, packages]);

  // Handle next/previous photo
  const handleNextPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
  };

  const handlePreviousPhoto = () => {
    setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  // Handle image preview click
  const handleImageClick = (imageUrl: string, alt: string) => {
    setPreviewImage(imageUrl);
    setPreviewAlt(alt);
    setPreviewOpen(true);
  };

  // Handle payment button click
  const handleProceedToPayment = async () => {
    if (!selectedPackageId) {
      setError("Please select a package");
      return;
    }

    if (!token) {
      setError("Invalid checkout link");
      return;
    }

    setCreatingPayment(true);
    try {
      const paymentResult = await createOrderPayment(
        token,
        selectedPackageId,
        orderNote.trim() || undefined,
      );

      if (paymentResult.paymentLink) {
        // Redirect to Square Checkout
        window.location.href = paymentResult.paymentLink;
      } else {
        setError("Failed to create payment link");
        setCreatingPayment(false);
      }
    } catch (err) {
      console.error("Error proceeding to payment:", err);
      setError(
        err instanceof Error ? err.message : "Failed to create payment link",
      );
      setCreatingPayment(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          backgroundColor: "#faf5ff",
        }}
      >
        <Stack alignItems="center" spacing={2}>
          <CircularProgress />
          <Typography variant="body1" color="text.secondary">
            Loading checkout...
          </Typography>
        </Stack>
      </Box>
    );
  }

  if (!order || !token) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Order information not found</Alert>
      </Container>
    );
  }

  const currentPhoto = photos.length > 0 ? photos[currentPhotoIndex] : null;

  return (
    <Box sx={{ backgroundColor: "#faf5ff", minHeight: "100vh", py: 4 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Complete Your Order
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Review your order details and photos before payment
          </Typography>
        </Box>

        {!!error && (
          <Alert severity="error" icon={<Warning />}>
            {error}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Left Column: Photos + Package Summary */}
          <Grid size={{ xs: 12, md: 7 }}>
            {/* Photo Preview */}
            {photos.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <Box
                  sx={{
                    backgroundColor: "#f1f5f9",
                    minHeight: 400,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {currentPhoto ? (
                    <Box
                      component="img"
                      src={currentPhoto.previewUrl}
                      alt={`Photo ${currentPhotoIndex + 1}`}
                      sx={{
                        maxWidth: "100%",
                        maxHeight: 400,
                        objectFit: "contain",
                      }}
                    />
                  ) : (
                    <ImageIcon sx={{ fontSize: 80, color: "text.disabled" }} />
                  )}
                </Box>

                {/* Photo Navigation */}
                {photos.length > 1 && (
                  <Box
                    sx={{
                      p: 2,
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Button
                      size="small"
                      onClick={handlePreviousPhoto}
                      disabled={currentPhotoIndex === 0}
                    >
                      ← Previous
                    </Button>
                    <Typography variant="body2" color="text.secondary">
                      {currentPhotoIndex + 1} of {photos.length}
                    </Typography>
                    <Button
                      size="small"
                      onClick={handleNextPhoto}
                      disabled={currentPhotoIndex === photos.length - 1}
                    >
                      Next →
                    </Button>
                  </Box>
                )}

                {/* Photo Thumbnails */}
                {photos.length > 1 && (
                  <Box
                    sx={{
                      p: 2,
                      borderTop: "1px solid #e2e8f0",
                      display: "flex",
                      gap: 1,
                      overflowX: "auto",
                    }}
                  >
                    {photos.map((photo, index) => (
                      <Box
                        key={photo.id}
                        component="img"
                        src={photo.previewUrl}
                        alt={`Thumbnail ${index + 1}`}
                        onClick={() => setCurrentPhotoIndex(index)}
                        sx={{
                          width: 60,
                          height: 60,
                          borderRadius: 1,
                          cursor: "pointer",
                          objectFit: "cover",
                          border:
                            currentPhotoIndex === index
                              ? "3px solid #892c8b"
                              : "1px solid #e2e8f0",
                          transition: "all 0.2s",
                          "&:hover": { borderColor: "#892c8b" },
                        }}
                      />
                    ))}
                  </Box>
                )}
              </Card>
            )}

            {/* Package Selection Card */}
            {packages.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <ShoppingBag sx={{ color: "secondary.main" }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      {order?.transaction?.squareOrderId
                        ? "Selected Package (Locked)"
                        : "Select Your Package"}
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <Stack spacing={2}>
                    <Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 1 }}
                      >
                        Package
                      </Typography>
                      <Select
                        value={selectedPackageId}
                        onChange={(e) => setSelectedPackageId(e.target.value)}
                        disabled={!!order?.transaction?.squareOrderId}
                        fullWidth
                      >
                        {packages.map((pkgs) => (
                          <MenuItem key={pkgs.packageId} value={pkgs.packageId}>
                            {pkgs.name} - ${pkgs.price?.toFixed(2)}
                          </MenuItem>
                        ))}
                      </Select>
                    </Box>

                    {pkg && (
                      <>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1.5 }}
                        >
                          Package Details:
                        </Typography>
                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                          {pkgPreviewImage.trim() && (
                            <Box
                              component="img"
                              src={pkgPreviewImage}
                              alt={`${pkg.name} preview`}
                              onClick={() =>
                                handleImageClick(
                                  pkgPreviewImage,
                                  `${pkg.name} preview`,
                                )
                              }
                              sx={{
                                flex: 1,
                                minWidth: "120px",
                                maxWidth: "250px",
                                height: "auto",
                                borderRadius: 1,
                                boxShadow: 1,
                                cursor: "pointer",
                                transition: "transform 0.2s ease-in-out",
                                "&:hover": { transform: "scale(1.05)" },
                              }}
                            />
                          )}
                          {pkgImage.trim() && (
                            <Box
                              component="img"
                              src={pkgImage}
                              alt={pkg.name}
                              onClick={() =>
                                handleImageClick(pkgImage, pkg.name)
                              }
                              sx={{
                                flex: 1,
                                minWidth: "120px",
                                maxWidth: "250px",
                                height: "auto",
                                borderRadius: 1,
                                boxShadow: 1,
                                cursor: "pointer",
                                transition: "transform 0.2s ease-in-out",
                                "&:hover": { transform: "scale(1.05)" },
                              }}
                            />
                          )}
                        </Box>

                        {/* Package Instructions/Description */}
                        {pkg.description && (
                          <Box
                            sx={{
                              mt: 2,
                              p: 2,
                              backgroundColor: "#f8fafc",
                              borderRadius: 1,
                              borderLeft: "4px solid",
                              borderLeftColor: "primary.main",
                            }}
                          >
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 1, fontWeight: 600 }}
                            >
                              Package Instructions:
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                whiteSpace: "pre-line",
                                color: "text.primary",
                                lineHeight: 1.6,
                              }}
                            >
                              {pkg.description}
                            </Typography>
                          </Box>
                        )}
                      </>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Order Note Section */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                  Note
                </Typography>
                <TextField
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="Add any special instructions or notes for your order..."
                  value={orderNote}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) {
                      setOrderNote(e.target.value);
                    }
                  }}
                  disabled={!!order?.transaction?.squareOrderId}
                  variant="outlined"
                  helperText={`${orderNote.length}/500 characters`}
                />
              </CardContent>
            </Card>
          </Grid>

          {/* Right Column: Order Summary + Payment */}
          <Grid size={{ xs: 12, md: 5 }}>
            {/* Order Details */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
                >
                  <ShoppingBag sx={{ color: "primary.main" }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Order Details
                  </Typography>
                </Box>
                <Divider sx={{ mb: 2 }} />

                <Stack spacing={2}>
                  {order.parentInfo && (
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Parent Name
                      </Typography>
                      <Typography variant="body1">
                        {order.parentInfo.name}
                      </Typography>
                    </Box>
                  )}

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Number of Photos
                    </Typography>
                    <Typography variant="body1">{photos.length}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Order Status
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mt: 0.5,
                      }}
                    >
                      <CheckCircle
                        sx={{
                          fontSize: 18,
                          color:
                            order.status === "pending_payment"
                              ? "#f59e0b"
                              : "#10b981",
                        }}
                      />
                      <Typography
                        variant="body1"
                        sx={{ textTransform: "capitalize" }}
                      >
                        {order.status === "pending_payment"
                          ? "Awaiting Payment"
                          : order.status}
                      </Typography>
                    </Box>
                  </Box>
                </Stack>
              </CardContent>
            </Card>

            {/* Price Summary */}
            {(() => {
              const packageAmount = pkg?.price || 0;

              return (
                <Paper sx={{ p: 3, backgroundColor: "#fef3c7", mb: 3 }}>
                  <Stack spacing={2}>
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="body1">Package Price</Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        ${packageAmount.toFixed(2)}
                      </Typography>
                    </Box>
                    <Divider />
                    <Box
                      sx={{ display: "flex", justifyContent: "space-between" }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        Total
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, color: "#892c8b" }}
                      >
                        ${packageAmount.toFixed(2)}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              );
            })()}

            {/* Payment Button */}
            <Button
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              startIcon={
                creatingPayment || redirecting ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <CreditCard />
                )
              }
              onClick={handleProceedToPayment}
              disabled={creatingPayment || redirecting || !selectedPackageId}
              sx={{ py: 1.5, fontSize: "1rem", fontWeight: 600 }}
            >
              {creatingPayment
                ? "Creating Payment..."
                : redirecting
                  ? "Redirecting..."
                  : "Proceed to Payment"}
            </Button>

            {/* Security Info */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", textAlign: "center", mt: 2 }}
            >
              🔒 Secure payment processed by Square
            </Typography>
          </Grid>
        </Grid>

        {/* Preview Modal */}
        <Dialog
          open={previewOpen}
          onClose={() => setPreviewOpen(false)}
          maxWidth="lg"
          // fullWidth
        >
          <DialogContent
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              py: 4,
              px: 2,
              position: "relative",
            }}
          >
            <Box
              component="img"
              src={previewImage}
              alt={previewAlt}
              sx={{ maxWidth: "90%", maxHeight: "80vh", objectFit: "contain" }}
            />
            <Button
              onClick={() => setPreviewOpen(false)}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                color: "black",
                minWidth: "auto",
                padding: 1,
              }}
            >
              ✕
            </Button>
          </DialogContent>
        </Dialog>
      </Container>
    </Box>
  );
};

export default Checkout;
